import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { log } from '@/lib/log';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useDrive = () => {
  const [connected, setConnected] = useState(false);
  const [folderConfigured, setFolderConfigured] = useState(false);
  const [folderName, setFolderName] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/drive/status`);
      setConnected(response.data.connected);
      setFolderConfigured(response.data.folder_configured);
      setFolderName(response.data.folder_name);
    } catch (error) {
      // Drive may not be connected yet; log for debugging
      log.error('[useDrive] status check failed:', error);
    }
  }, []);

  useEffect(() => {
    checkStatus();
    
    // Listen for Drive connection message
    const handleMessage = (event) => {
      if (event.data?.type === 'drive_connected') {
        checkStatus();
        toast.success('Google Drive connected successfully!');
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [checkStatus]);

  const connectDrive = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/drive/connect`);
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      
      window.open(
        response.data.authorization_url,
        'Google Drive Authorization',
        `width=${width},height=${height},left=${left},top=${top}`
      );
    } catch (error) {
      toast.error('Failed to connect Google Drive');
    } finally {
      setLoading(false);
    }
  }, []);

  const getFolders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/drive/folders`);
      return response.data.folders;
    } catch (error) {
      toast.error('Failed to fetch folders');
      return [];
    }
  }, []);

  const createFolder = useCallback(async (folderName) => {
    try {
      const response = await axios.post(`${API}/drive/folder/create?folder_name=${encodeURIComponent(folderName)}`);
      return response.data.folder;
    } catch (error) {
      toast.error('Failed to create folder');
      return null;
    }
  }, []);

  const saveSettings = useCallback(async (folderId, folderName) => {
    try {
      await axios.post(`${API}/drive/settings?folder_id=${folderId}&folder_name=${encodeURIComponent(folderName)}`);
      setFolderConfigured(true);
      setFolderName(folderName);
      toast.success('Drive folder configured!');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  }, []);

  const uploadToDrive = useCallback(async (clientName, templateIds, fieldValues) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/drive`, {
        client_name: clientName,
        template_ids: templateIds,
        field_values: fieldValues
      });

      if (response.data.success) {
        toast.success(`✓ Uploaded ${response.data.uploaded_files.length} documents to Google Drive`);
        return response.data.uploaded_files;
      }
    } catch (error) {
      toast.error('Failed to upload to Google Drive');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    connected,
    folderConfigured,
    folderName,
    loading,
    connectDrive,
    getFolders,
    createFolder,
    saveSettings,
    uploadToDrive,
    refreshStatus: checkStatus
  };
};
